import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { ApiService } from "@app/services/api.service";
import { CrudService } from "@app/services/crud.service";
import { KbService } from "@app/services/kb.service";
import { CrudController, ICrudScope } from "@app/views/crud.view";
import { IKbRole, IKbUserEdit, INameValue, INotificationSetting, eAlertType, eNotificationSetting, eViewType, IRootScope, eUserLicenseType } from "@models";
import { icons } from "@tools";

export interface IUserScope extends ICrudScope<IKbUserEdit> {
    canResetPassword: boolean;
    canChangePassword: boolean;
    resetPassword: () => void;
    canEdit: boolean;
    roles: IKbRole[];
    notificationOptions: INameValue[];
    pendingApprovalNotificationOptions: INameValue[];
    notificationSetting: INotificationSetting;
    deleteAndAnonymize: () => void;
    isReadOnlySso: boolean;
    resetDeactivation: () => void;
    channels: INameValue[];
}

@NgView({
    token: Token.UserView,
    inherit: [Token.CrudView],
    dependencies: [
        Token.$RootScope,
        Token.KbService
    ]
})
export class UserController extends CrudController<IKbUserEdit> {

    constructor(
        public $scope: IUserScope,
        crudService: CrudService,
        model: IKbUserEdit,
        public $rootScope: IRootScope,
        kbService: KbService
    ) {
        super(
            $scope,
            crudService,
            model
        );

        $scope.isReadOnlySso = !$rootScope.context.ssoManualUserManagement && model.isSsoUser;

        this.drawerService.setHelpUrl("Users");

        this.$http.get("/api/notificationsettings").then(response => {
            $scope.notificationSetting = response.data;
        });

        if ($rootScope.user.isCompanyAdmin) {
            this.api.channels.search({
                sortField: "name"
            }).then(channels => {
                $scope.channels = channels.map(c => {
                    return {
                        name: c.name,
                        value: c.id.toString()
                    };
                });
                $scope.channels.unshift({
                    name: "-None-",
                    value: null
                });
            });
        }

        if ($scope.model) {
            crudService.$rootScope.setTitle($scope.model.firstName + " " + $scope.model.lastName);
        }

        if (this.$state.current.data.viewType == eViewType.new) {
            crudService.$rootScope.setTitle(loc.create + " " + loc.user);

            // create a new blank user
            $scope.model = {
                isApproved: true,
                licenseType: eUserLicenseType.internal,
                shipToBillingAddress: true,
                roles: []
            };
        }

        this.api.roles.search({
            sortField: "role",
        }).then(r => {
            $scope.roles = r.filter(ur => ur.id != -1);
        });

        $scope.resetDeactivation = () => {
            this.api.users.resetDeactivation(this.$stateParams.id).then(() => {
                this.dialogService.alert({
                    type: eAlertType.success,
                    msg: loc.msg_resetdeactivationcomplete
                });
            });
        }

        $scope.resetPassword = () => {
            this.api.users.resetPassword(this.$stateParams.id).then(() => {
                this.dialogService.alert({
                    type: eAlertType.success,
                    msg: loc.msg_passwordreset.format($scope.model.email)
                });
            });
        };

        $scope.deleteAndAnonymize = () => {
            this.dialogService.confirm("Are you sure you want to delete and permanently anonymize this user? This cannot be undone!", () => {
                this.api.users.deleteAndAnonymize(this.$stateParams.id).then(() => {
                    this.$state.go("kb.admin.users");
                });
            });
        }

        this.deleteMsg = loc.msg_deleteuser.format(this.$scope.model.firstName + " " + this.$scope.model.lastName);

        $scope.canEdit = false;
        $scope.canResetPassword = false;
        $scope.canChangePassword = false;

        // determine whether to show edit buttons
        if (this.$rootScope.user.id == this.$stateParams.id) {
            // if (this.$rootScope.user.canModifySelf) {
            this.$scope.canEdit = true;
            this.$scope.canChangePassword = true;
            // }
        } else {
            if (this.$rootScope.user.canModifyUsers) {
                this.$scope.canEdit = true;
                this.$scope.canResetPassword = true;
            }
        }

        $scope.notificationOptions = kbService.getEnumSelects(eNotificationSetting, "eNotificationSetting");
        $scope.pendingApprovalNotificationOptions = [
            {
                name: loc.enotificationsetting_email_title,
                value: eNotificationSetting.email
            },
            {
                name: loc.enotificationsetting_none_title,
                value: eNotificationSetting.none
            },
            {
                name: loc.enotificationsetting_companydefault_title,
                value: eNotificationSetting.companyDefault
            },
        ];

        // add drawers
        if (!$scope.model.isSsoUser) {
            let drChangePassword = this.drawerService.addDrawer(icons.change, loc.changepassword, () => {
                this.$state.go("kb.changePassword", { id: $scope.model.id });
            }, $scope.canChangePassword && !$scope.isNew);

            let drResetPassword = this.drawerService.addDrawer(
                icons.change,
                loc.resetpassword,
                $scope.resetPassword,
                $scope.canResetPassword && !$scope.isNew
            );
        }
        let drEdit = this.drawerService.addDrawer(icons.edit, loc.edit, () => {
            this.$state.go("kb.admin.userEdit", { id: $scope.model.id });
        }, $scope.canEdit && $scope.isView);
        let drDelete = this.drawerService.addDrawer(
            icons.deleter,
            loc.deleter,
            $scope.delete,
            $scope.canEdit && !$scope.isNew
        );
        let drDeleteAndAnonymize = this.drawerService.addDrawer(
            icons.anonymize,
            loc.deleteandanonymize,
            $scope.deleteAndAnonymize,
            $scope.canEdit && !$scope.isNew
        );
        let drResetDeactivations = this.drawerService.addDrawer(
            icons.action, loc.resetdeactivationtimer, $scope.resetDeactivation, $rootScope.companySettings.accountIdleExpirationInDays > 0 && $scope.isEdit && $rootScope.context.user.canModifyUsers);
        let drSave = this.drawerService.addDrawer(
            icons.save, loc.save, $scope.save, $scope.isEdit || $scope.isNew);
        let drCancel = this.drawerService.addDrawer(
            icons.cancel, loc.cancel, $scope.cancel, $scope.isEdit || $scope.isNew);
    }

    public client(api: ApiService) {
        return api.users;
    }

    public waitForRefresh() {
        return true;
    }

    public onSuccess() {
        if (this.$rootScope.user.isCompanyAdmin) {
            this.$state.go("kb.admin.users");
        } else {
            this.$state.go("kb.products");
        }
    }

    public onDelete() {
        this.$state.go("kb.admin.users");
    }

}
